// Global Instructions Rule Applied!
// Distributions service (docs/WATERFALL_CARRY_DESIGN.md, phase 3): the HTTP
// calls of the distribution form and the fund's Distributions tab. Every
// writer returns the refreshed, validated detail; a server-side refusal
// surfaces as `error` with the RPC's own sentence (json.error || json.message).

import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import { filenameFromContentDisposition } from '../../../core/utils/contentDisposition.ts'
import { saveBlob } from '../../document_reports/services/documentReportsService.ts'
import {
  DistributionDetail,
  DistributionListRow,
  DistributionSavePayload,
  DistributionSourceOption,
  validateDistributionDetail,
  validateDistributionListRows,
  validateDistributionSourceOptions
} from '../../../state_manager/data_models/distribution_detail.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

const authHeaders = (accessToken: string | null): HeadersInit => ({
  'Content-Type': 'application/json',
  ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {})
})

export interface DistributionDetailResponse {
  data: DistributionDetail | null
  error?: string
}

const errorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : 'An unexpected error occurred'

export const fetchDistributionDetail = async (
  distributionId: string,
  accessToken: string | null = null
): Promise<DistributionDetailResponse> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DISTRIBUTIONS.GET, { id: distributionId }, {})
    const response = await fetch(url, { method: API_ROUTES.DISTRIBUTIONS.GET.method, headers: authHeaders(accessToken) })

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      if (response.status === 404) throw new Error('Distribution not found')
      throw new Error(`Failed to fetch distribution: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: unknown }
    if (!json.data) return { data: null }
    return { data: validateDistributionDetail(json.data) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching distribution detail:', error)
    return { data: null, error: errorMessage(error) }
  }
}

/**
 * Create or update a Draft distribution (header, the replacement set of
 * sources, the overrides - one per partner and class, and per row for the
 * priority lines - and the SBA override reason). A refusal
 * (the sources do not add up to the gross, an Issued record) comes back as
 * the RPC's sentence.
 */
export const saveDistribution = async (
  payload: DistributionSavePayload,
  accessToken: string | null = null
): Promise<DistributionDetailResponse> => {
  try {
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DISTRIBUTIONS.SAVE), {
      method: API_ROUTES.DISTRIBUTIONS.SAVE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: payload })
    })

    const json = (await response.json().catch(() => ({}))) as { data?: unknown; message?: string; error?: string }

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(json.message || json.error || `Failed to save distribution: ${response.status}`)
    }

    if (!json.data) return { data: null }
    return { data: validateDistributionDetail(json.data) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error saving distribution:', error)
    return { data: null, error: errorMessage(error) }
  }
}

type DistributionActionRoute =
  | typeof API_ROUTES.DISTRIBUTIONS.COMPUTE
  | typeof API_ROUTES.DISTRIBUTIONS.ISSUE
  | typeof API_ROUTES.DISTRIBUTIONS.REVERT

/**
 * Lifecycle actions (compute / issue / revert) return the refreshed detail on
 * success; a server-side refusal surfaces as `error` with its sentence.
 */
async function postDistributionAction(
  route: DistributionActionRoute,
  distributionId: string,
  accessToken: string | null
): Promise<DistributionDetailResponse> {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, route, { id: distributionId }, {})
    const response = await fetch(url, { method: route.method, headers: authHeaders(accessToken) })
    const json = (await response.json().catch(() => ({}))) as { data?: unknown; message?: string; error?: string }

    if (!response.ok) {
      return { data: null, error: json.error || json.message || `Request failed: ${response.status}` }
    }

    if (!json.data) return { data: null }
    return { data: validateDistributionDetail(json.data) }
  } catch (error) {
    return { data: null, error: errorMessage(error) }
  }
}

export const computeDistribution = (distributionId: string, accessToken: string | null = null) =>
  postDistributionAction(API_ROUTES.DISTRIBUTIONS.COMPUTE, distributionId, accessToken)

export const issueDistribution = (distributionId: string, accessToken: string | null = null) =>
  postDistributionAction(API_ROUTES.DISTRIBUTIONS.ISSUE, distributionId, accessToken)

export const revertDistribution = (distributionId: string, accessToken: string | null = null) =>
  postDistributionAction(API_ROUTES.DISTRIBUTIONS.REVERT, distributionId, accessToken)

/** Soft-trash a Draft distribution. */
export const trashDistribution = async (
  distributionId: string,
  accessToken: string | null = null
): Promise<{ success: boolean; error?: string }> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DISTRIBUTIONS.DELETE, { id: distributionId }, {})
    const response = await fetch(url, { method: API_ROUTES.DISTRIBUTIONS.DELETE.method, headers: authHeaders(accessToken) })
    if (!response.ok) {
      const json = (await response.json().catch(() => ({}))) as { message?: string; error?: string }
      return { success: false, error: json.error || json.message || `Failed to delete distribution: ${response.status}` }
    }
    return { success: true }
  } catch (error) {
    return { success: false, error: errorMessage(error) }
  }
}

/**
 * Download the audit workbook of a distribution (the allocation with the
 * tier arithmetic as formulas) and save it under the server's filename. The
 * server refuses a distribution without lines with its own sentence.
 */
export const downloadDistributionWorkbook = async (
  distributionId: string,
  accessToken: string | null = null
): Promise<{ success: boolean; error?: string }> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DISTRIBUTIONS.WORKBOOK, { id: distributionId }, {})
    const response = await fetch(url, {
      method: API_ROUTES.DISTRIBUTIONS.WORKBOOK.method,
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {}
    })
    if (!response.ok) {
      if (response.status === 401) return { success: false, error: 'Authentication failed - please log in again' }
      const json = (await response.json().catch(() => ({}))) as { message?: string; error?: string }
      return { success: false, error: json.message || json.error || `Failed to download the workbook: ${response.status}` }
    }
    const blob = await response.blob()
    saveBlob(blob, filenameFromContentDisposition(response.headers.get('Content-Disposition'), 'distribution-allocation.xlsx'))
    return { success: true }
  } catch (error) {
    return { success: false, error: errorMessage(error) }
  }
}

/** The distributions of a fund (the fund Distributions tab). */
export const fetchFundDistributions = async (
  fundId: string,
  accessToken: string | null = null
): Promise<{ data: DistributionListRow[]; error?: string }> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DISTRIBUTIONS.FUND_LIST, { fundId }, {})
    const response = await fetch(url, { method: API_ROUTES.DISTRIBUTIONS.FUND_LIST.method, headers: authHeaders(accessToken) })
    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(`Failed to fetch the fund's distributions: ${response.status} ${response.statusText}`)
    }
    const json = (await response.json()) as { data?: unknown }
    return { data: validateDistributionListRows(json.data ?? []) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching fund distributions:', error)
    return { data: [], error: errorMessage(error) }
  }
}

/** The Booked realizations of a fund a distribution can name as its sources. */
export const fetchDistributionSourceOptions = async (
  fundId: string,
  accessToken: string | null = null
): Promise<{ data: DistributionSourceOption[]; error?: string }> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DISTRIBUTIONS.SOURCE_OPTIONS, { fundId }, {})
    const response = await fetch(url, {
      method: API_ROUTES.DISTRIBUTIONS.SOURCE_OPTIONS.method,
      headers: authHeaders(accessToken)
    })
    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(`Failed to fetch the realizations: ${response.status} ${response.statusText}`)
    }
    const json = (await response.json()) as { data?: unknown }
    return { data: validateDistributionSourceOptions(json.data ?? []) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching distribution source options:', error)
    return { data: [], error: errorMessage(error) }
  }
}
